package article.spring

import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.animateIntOffsetAsState
import androidx.compose.animation.core.spring
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.onPointerEvent
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.LayoutCoordinates
import androidx.compose.ui.layout.boundsInWindow
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.positionInParent
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlin.math.roundToInt


const val SPRING_INTRO = "40cb2648970de46bd352a6ff55c4539ef77cc438ccfff5123d257b3f1dd24b74"

val style = TextStyle(
    fontWeight = FontWeight.Bold,
    fontSize = 36.sp,
)

@Composable
fun SpringIntro() {
    Box(
        Modifier
            .background(
                Brush.linearGradient(
                    listOf(
                        Color(0xfff9e7bb),
                        Color(0xffe97cbb),
                        Color(0xff3d47d9),
                    )
                ),
            )
            .fillMaxSize()
    ) {

        SpringGrid(color = Color.Black.copy(alpha = .05f)) {  }

        val density = LocalDensity.current
        var springForS by remember { mutableStateOf(Rect.Zero) }
        var springForP by remember { mutableStateOf(Rect.Zero) }
        var springForR by remember { mutableStateOf(Rect.Zero) }
        var springForI by remember { mutableStateOf(Rect.Zero) }
        var springForN by remember { mutableStateOf(Rect.Zero) }
        var springForG by remember { mutableStateOf(Rect.Zero) }

        listOf(
            springForS,
            springForP,
            springForR,
            springForI,
            springForN,
            springForG
        ).forEachIndexed { index, rect ->
            Box(
                Modifier
                    .offset { IntOffset(rect.left.toInt(), rect.top.toInt()) }
                    .width(with(density) { rect.right.toDp() })
                    .height(with(density) { rect.bottom.toDp() }),
                contentAlignment = Alignment.Center,
            ) {
                if (index == 0 || index == 5) {
                    SpringCoil(7, SpringCoilOrientation.Horizontal, Color.White)
                } else {
                    SpringCoil(12, SpringCoilOrientation.Vertical, Color.White)
                }
            }
        }

        Row(
            modifier = Modifier
                .fillMaxSize()
                .align(Alignment.Center),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center,
        ) {
            SpringyLetter("S", SpringTarget.Left) { springForS = it }
            SpringyLetter("P", SpringTarget.Top) { springForP = it }
            SpringyLetter("R", SpringTarget.Bottom) { springForR = it }
            SpringyLetter("I", SpringTarget.Top) { springForI = it }
            SpringyLetter("N", SpringTarget.Bottom) { springForN = it }
            SpringyLetter("G", SpringTarget.Right) { springForG = it }
        }
    }
}

@OptIn(ExperimentalComposeUiApi::class)
@Composable
private fun SpringyLetter(
    letter: String,
    target: SpringTarget,
    modifier: Modifier = Modifier,
    setSpringBounds: (Rect) -> Unit,
) {
    var switch by remember { mutableStateOf(false) }

    var layoutCoordinates: LayoutCoordinates? by remember { mutableStateOf(null) }

    val offset: IntOffset by remember {
        derivedStateOf {
            layoutCoordinates?.let {
                when (target) {
                    SpringTarget.Top -> if (switch) IntOffset(
                        0, -it.boundsInWindow().height.roundToInt()
                    ) else IntOffset.Zero

                    SpringTarget.Bottom -> if (switch) IntOffset(
                        0, it.boundsInWindow().height.roundToInt()
                    ) else IntOffset.Zero

                    SpringTarget.Left -> if (switch) IntOffset(
                        -it.boundsInWindow().width.roundToInt(), 0
                    ) else IntOffset.Zero

                    SpringTarget.Right -> {
                        if (switch) IntOffset(
                            it.boundsInWindow().width.roundToInt(), 0
                        ) else IntOffset.Zero
                    }
                }
            } ?: IntOffset.Zero
        }
    }

    val animatedOffset by animateIntOffsetAsState(
        targetValue = offset,
        animationSpec = spring(
            dampingRatio = Spring.DampingRatioHighBouncy,
            stiffness = Spring.StiffnessVeryLow,
        )
    )


    layoutCoordinates?.let { layoutCoordinates ->
        when (target) {
            SpringTarget.Bottom -> {
                setSpringBounds(
                    Rect(
                        left = layoutCoordinates.positionInParent().x,
                        top = (layoutCoordinates.positionInParent().y + layoutCoordinates.boundsInWindow().height) + animatedOffset.y,
                        right = layoutCoordinates.boundsInWindow().width,
                        bottom = (layoutCoordinates.parentLayoutCoordinates?.boundsInWindow()?.height
                            ?: 0f) - ((layoutCoordinates.positionInParent().y) + animatedOffset.y),
                    )
                )
            }

            SpringTarget.Left -> {
                setSpringBounds(
                    Rect(
                        left = 0f,
                        top = layoutCoordinates.positionInParent().y,
                        right = (layoutCoordinates.positionInParent().x + animatedOffset.x).coerceAtLeast(
                            0f
                        ),
                        bottom = layoutCoordinates.boundsInWindow().height,
                    )
                )
            }

            SpringTarget.Right -> {
                setSpringBounds(
                    Rect(
                        left = (layoutCoordinates.positionInParent().x + layoutCoordinates.boundsInWindow().width) + animatedOffset.x,
                        top = layoutCoordinates.positionInParent().y,
                        right = (layoutCoordinates.parentLayoutCoordinates?.boundsInWindow()?.width
                            ?: 0f) - ((layoutCoordinates.positionInParent().x) + animatedOffset.x),
                        bottom = layoutCoordinates.boundsInWindow().height,
                    )
                )
            }

            SpringTarget.Top -> {
                setSpringBounds(
                    Rect(
                        left = layoutCoordinates.positionInParent().x,
                        top = 0f,
                        right = layoutCoordinates.boundsInWindow().width,
                        bottom = layoutCoordinates.positionInParent().y + animatedOffset.y,
                    )
                )
            }
        }
    }

    Text(
        letter,
        modifier = modifier
            .onPointerEvent(PointerEventType.Enter) {
                switch = !switch
            }
            .onPointerEvent(PointerEventType.Exit) {
                switch = !switch
            }
            .padding(2.dp)
            .onGloballyPositioned { layoutCoordinates = it }
            .offset { animatedOffset }
            .shadow(16.dp, shape = RoundedCornerShape(8.dp))
            .background(Color.White, shape = RoundedCornerShape(8.dp))
            .padding(12.dp)
            .pointerInput(Unit) {
                detectTapGestures {
                    switch = !switch
                }
            },
        textAlign = TextAlign.Center,
        style = style,
        color = Color.Black,
    )
}

private sealed class SpringTarget {
    data object Top : SpringTarget()
    data object Bottom : SpringTarget()
    data object Left : SpringTarget()
    data object Right : SpringTarget()
}